/**
 * La **liste de filtre** — la pilule du dessin, et une liste dessinée avec les
 * mêmes jetons.
 *
 * Le constat du porteur, dans Chrome : « là où un champ a une liste fermée de
 * valeurs, l'écran offre une zone de texte nue ». Le journal d'audit demandait
 * le type d'entité en **énumérant les codes dans son propre libellé** — « Type
 * d'entité — CUSTOMER, KYC_REVIEW, BO_USER… » — puis laissait l'opérateur les
 * taper. Un libellé qui énumère est l'aveu qu'il manque une liste.
 *
 * ## Pourquoi pas un `<select>` natif
 *
 * Parce qu'il peint l'interface du **système** : sa police, ses formes, sa
 * couleur de survol, et sur macOS une liste qui ignore entièrement les jetons.
 * À côté d'une pilule dessinée, il se voit emprunté — et c'est ce que le
 * porteur reproche au produit ce soir. La liste est donc bâtie, sur le système
 * de design.
 *
 * **Ce que le natif donnait gratuitement est repris à la main, en entier.**
 * C'est la contrepartie, et elle n'est pas facultative : une liste qui ne
 * marcherait qu'à la souris serait une **régression** contre la zone de texte
 * qu'elle remplace, puisque celle-là marchait au clavier.
 *
 * ## La barre de recherche, et pourquoi pas partout
 *
 * Le porteur l'a demandée en propre : « les listes déroulantes doivent être des
 * listes déroulantes avec une barre de recherche ». Le saut à la première
 * lettre suffisait aux sept types d'entité d'alors ; il ne suffit pas au filtre
 * « Utilisateur » du journal d'audit, dont les valeurs sont les acteurs
 * réellement présents dans la fenêtre — il peut y en avoir cent, et ils
 * commencent tous par les mêmes lettres.
 *
 * **Le seuil n'est pas une affaire de goût : c'est ce que le panneau montre.**
 * `.filter-panel--list` est plafonné à `4 × space.scale.10` (256 points) et
 * chaque option mesure `space.scale.7` (32) : **huit** options tiennent dans le
 * panneau sans le faire défiler. En deçà, la liste est entière sous les yeux —
 * chercher n'apporte rien, et un champ de plus coûterait un Tab de plus à
 * chaque ouverture. Au-delà, l'opérateur choisit dans une liste dont il ne voit
 * pas la fin, et c'est exactement là qu'une recherche gagne. Les types
 * d'entité en ont reçu un le jour où le reporting en a apporté un neuvième —
 * huit tenaient sans défiler, neuf non ; les acteurs et les types d'action du
 * journal en reçoivent un dès qu'ils dépassent la fenêtre. `RANGS_VISIBLES`
 * porte ce nombre, et `controles-de-filtre.test.tsx` le **relit dans la
 * feuille de style** plutôt que de le recopier : si le plafond du panneau
 * change, le seuil change avec lui ou le contrôle rougit.
 *
 * ## Le patron ARIA, et le seul endroit où il bascule
 *
 * **Sans champ de recherche** — le patron `combobox` en lecture seule :
 *
 *  - le **déclencheur** porte `role="combobox"`, `aria-expanded`,
 *    `aria-controls` et `aria-activedescendant` ; le focus ne le quitte jamais ;
 *  - `↓` `↑` déplacent, `Début` et `Fin` vont aux extrémités, une lettre tapée
 *    saute à la première option qui commence par elle, `Entrée` choisit,
 *    `Échap` ferme **et rend le focus au déclencheur**.
 *
 * **Avec champ de recherche** — le patron `combobox` éditable. Le rôle
 * `combobox` **se déplace sur le champ**, et le déclencheur redevient un bouton
 * qui déploie (`aria-haspopup="listbox"`, `aria-expanded`). C'est là que les
 * listes bâties à la main se cassent d'ordinaire : deux `combobox` imbriqués,
 * ou un `aria-activedescendant` laissé sur un déclencheur qui n'a plus le
 * focus, et le lecteur d'écran annonce l'option d'un élément que personne ne
 * regarde. Ici :
 *
 *  - **le focus vit dans le champ** tant que le panneau est ouvert, et c'est le
 *    champ qui porte `aria-expanded`, `aria-controls` et
 *    `aria-activedescendant` ;
 *  - la saisie filtre ; `↓` `↑` déplacent la surbrillance **sans** déplacer le
 *    focus ; `Entrée` choisit l'option en surbrillance ; `Échap` ferme et rend
 *    le focus au déclencheur ;
 *  - **quand rien ne correspond, le panneau le dit.** Une boîte vide se lit
 *    comme un contrôle cassé ;
 *  - la liste porte un **nom accessible** qui cite le libellé du champ, et le
 *    **nombre de correspondances** est annoncé par une région vivante à chaque
 *    frappe. Sans elle, un opérateur non voyant taperait dans le vide.
 *
 * La **forme du déclencheur ne change pas d'un cas à l'autre** : c'est la même
 * pilule que le calendrier et que la saisie libre, parce que la rangée de
 * filtres doit se lire comme une rangée.
 *
 * ## Ce qui part n'est JAMAIS ce qui s'affiche
 *
 * L'option porte une `value` — le code que le serveur sert, `CUSTOMER`,
 * `KYC_REVIEW` — et un `label`, qui est ce que l'opérateur lit. C'est la
 * `value` qui part vers la route, toujours. Sur le journal d'audit les deux
 * coïncident, et c'est délibéré : la colonne « Cible » de l'écran affiche ces
 * codes-là, parce que ce sont ceux que le serveur rend, et une liste qui dirait
 * « Client » sous une table qui répond « CUSTOMER » serait deux vocabulaires
 * pour une seule chose. Le composant, lui, sépare les deux **en tout temps** —
 * et `controles-de-filtre.test.tsx` l'éprouve sur des options dont le libellé
 * diffère du code, sans quoi l'égalité rendrait le cas creux.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';

import { useTranslation } from 'react-i18next';

import { FilterPill } from './FilterPill.js';
import { useCloseOnOutsidePointer } from './use-dismiss.js';

/**
 * Le nombre d'options que le panneau montre **sans défiler**.
 *
 * `.filter-panel--list` plafonne sa hauteur à `4 × space.scale.10` = 256
 * points, et `.filter-panel__option` mesure `space.scale.7` = 32 : huit
 * options. C'est le seuil à partir duquel la barre de recherche apparaît — pas
 * un chiffre choisi au jugé, mais **ce que l'œil voit d'un coup**.
 */
export const RANGS_VISIBLES = 8;

/**
 * Une valeur de la liste.
 *
 * `value` part vers la route ; `label` s'affiche. Les confondre est la faute
 * que ce type existe pour rendre impossible.
 */
export interface FilterSelectOption {
  readonly value: string;
  readonly label: string;
}

/** Ce que la liste attend. */
export interface FilterSelectProps {
  /** L'identifiant du déclencheur, celui que le libellé de la pilule désigne. */
  readonly controlId: string;
  /** Clé de traduction du libellé. */
  readonly labelKey: string;
  /** Clé de traduction de l'invite — ce qui s'affiche tant que rien n'est choisi. */
  readonly placeholderKey: string;
  /** Les valeurs proposées. **Une liste vide est refusée**, voir plus bas. */
  readonly options: readonly FilterSelectOption[];
  /** Le code actuellement choisi, ou la chaîne vide. */
  readonly value: string;
  /** Appelé avec le **code** choisi. Jamais avec le libellé. */
  readonly onSelect: (value: string) => void;
  /** Le champ est-il en erreur ? */
  readonly invalid?: boolean;
  /** L'identifiant du message d'erreur, pour `aria-describedby`. */
  readonly describedBy?: string;
}

/**
 * Le texte, ramené à ce qui se compare.
 *
 * Sans les diacritiques et sans la casse : un opérateur qui cherche « reseau »
 * doit trouver « Réseau ». Taper l'accent juste n'est pas une condition d'accès
 * à ses propres données.
 */
function pourComparer(texte: string): string {
  return texte
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();
}

/** La liste du dessin. */
export function FilterSelect({
  controlId,
  labelKey,
  placeholderKey,
  options,
  value,
  onSelect,
  invalid,
  describedBy,
}: FilterSelectProps): ReactNode {
  const { t } = useTranslation();
  const declencheur = useRef<HTMLButtonElement | null>(null);
  const contenant = useRef<HTMLSpanElement | null>(null);
  const champ = useRef<HTMLInputElement | null>(null);
  const [ouvert, setOuvert] = useState(false);
  const [actif, setActif] = useState(0);
  const [recherche, setRecherche] = useState('');

  if (options.length === 0) {
    // Une liste sans valeur est un contrôle qui n'offre rien : elle se lit
    // comme un champ cassé, et elle passerait pour un champ vide. Le silence
    // serait pire — c'est la vacuité, au niveau du composant.
    throw new Error(
      `ÉCHEC : la liste « ${controlId} » ne propose aucune valeur ; ` +
        `un contrôle qui n'offre rien ne doit pas atteindre l'écran.`,
    );
  }

  /** Cette liste dépasse-t-elle ce que le panneau montre d'un coup ? */
  const avecRecherche = options.length > RANGS_VISIBLES;

  const idPanneau = `${controlId}-panneau`;
  const idListe = avecRecherche ? `${controlId}-liste` : idPanneau;
  const idChamp = `${controlId}-recherche`;
  const idOption = (rang: number): string => `${idPanneau}-${rang}`;

  /**
   * Les options **affichées** — toutes, ou celles que la saisie retient.
   *
   * Tout ce qui suit — la surbrillance, `aria-activedescendant`, le choix —
   * raisonne sur CETTE liste, jamais sur `options` : sinon `Entrée` choisirait
   * la troisième option de la liste entière alors que l'opérateur regarde la
   * troisième option filtrée.
   */
  const affichees = useMemo(() => {
    const motif = pourComparer(recherche.trim());
    if (!avecRecherche || motif === '') return options;
    return options.filter((option) => pourComparer(option.label).includes(motif));
  }, [avecRecherche, options, recherche]);

  const choisi = affichees.findIndex((option) => option.value === value);
  const retenu = options.find((option) => option.value === value);
  const affichee = retenu === undefined ? t(placeholderKey) : retenu.label;

  const fermer = useCallback(() => {
    setOuvert(false);
    // La saisie ne survit pas à la fermeture : rouvrir sur un filtre posé la
    // fois d'avant montrerait une liste amputée sans dire pourquoi.
    setRecherche('');
  }, []);
  useCloseOnOutsidePointer(contenant, ouvert, fermer);

  /**
   * Ferme et **rend le focus au déclencheur** — jamais l'un sans l'autre.
   *
   * Les trois chemins en passent par là : `Échap`, le choix d'une ligne à la
   * souris, et `Entrée` dans le champ de recherche. Sans lui, un clic sur une
   * option laisserait le focus sur le corps du document — ou, pire avec un
   * champ de recherche, sur un élément que React vient de démonter — et la
   * touche suivante repartirait du haut de l'écran.
   */
  const fermerEtRevenir = useCallback(() => {
    setOuvert(false);
    setRecherche('');
    declencheur.current?.focus();
  }, []);

  const ouvrirSur = useCallback(
    (rang: number) => {
      setActif(Math.min(Math.max(rang, 0), options.length - 1));
      setOuvert(true);
    },
    [options.length],
  );

  /**
   * Le focus part dans le champ de recherche à l'ouverture.
   *
   * Au tour de rendu suivant, et par un effet plutôt qu'à la main : le champ
   * n'existe pas encore au moment du clic sur le déclencheur. C'est ce qui rend
   * vraie la phrase « le focus vit dans le champ » — sans lui, la première
   * frappe repartirait au déclencheur.
   */
  useEffect(() => {
    if (ouvert && avecRecherche) champ.current?.focus();
  }, [avecRecherche, ouvert]);

  const choisirLeRang = useCallback(
    (rang: number) => {
      const option = affichees[rang];
      // Aucune option sous la surbrillance — la saisie ne retient rien : il n'y
      // a rien à choisir, et choisir « la première de la liste entière » serait
      // choisir à la place de l'opérateur.
      if (option === undefined) return;
      // Le CODE, jamais le libellé : c'est lui que la route prend.
      onSelect(option.value);
      setActif(rang);
      fermerEtRevenir();
    },
    [affichees, fermerEtRevenir, onSelect],
  );

  /** Le premier rang dont le libellé commence par [lettre], à partir de [depuis]. */
  const rangParLettre = useCallback(
    (lettre: string, depuis: number): number => {
      const normalisee = pourComparer(lettre);
      for (let saut = 1; saut <= affichees.length; saut += 1) {
        const rang = (depuis + saut) % affichees.length;
        if (pourComparer(affichees[rang].label).startsWith(normalisee)) return rang;
      }
      return -1;
    },
    [affichees],
  );

  /** Déplace la surbrillance de [pas], sans jamais sortir de la liste affichée. */
  const deplacer = useCallback(
    (pas: number) => {
      setActif((rang) => Math.min(Math.max(rang + pas, 0), Math.max(affichees.length - 1, 0)));
    },
    [affichees.length],
  );

  const auClavier = useCallback(
    (evenement: KeyboardEvent<HTMLButtonElement>) => {
      const depart = choisi < 0 ? 0 : choisi;
      switch (evenement.key) {
        case 'ArrowDown':
          evenement.preventDefault();
          if (!ouvert) ouvrirSur(depart);
          else deplacer(1);
          return;
        case 'ArrowUp':
          evenement.preventDefault();
          if (!ouvert) ouvrirSur(depart);
          else deplacer(-1);
          return;
        case 'Home':
          evenement.preventDefault();
          if (!ouvert) ouvrirSur(0);
          else setActif(0);
          return;
        case 'End':
          evenement.preventDefault();
          if (!ouvert) ouvrirSur(affichees.length - 1);
          else setActif(affichees.length - 1);
          return;
        case 'Enter':
        case ' ':
          evenement.preventDefault();
          if (!ouvert) ouvrirSur(depart);
          else choisirLeRang(actif);
          return;
        case 'Escape':
          if (!ouvert) return;
          evenement.preventDefault();
          // Le focus revient au déclencheur : un panneau fermé qui laisserait
          // le focus nulle part renvoie l'opérateur au début du document.
          fermerEtRevenir();
          return;
        case 'Tab':
          // La tabulation quitte le champ : le panneau ne doit pas rester posé
          // par-dessus l'écran suivant. Aucun `preventDefault` — on ne retient
          // pas le focus.
          if (ouvert) fermer();
          return;
        default:
          break;
      }
      // Une lettre tapée. La seule touche imprimable, et jamais une
      // combinaison : `Ctrl+F` du navigateur reste au navigateur.
      if (evenement.key.length !== 1 || evenement.ctrlKey || evenement.metaKey || evenement.altKey) {
        return;
      }
      evenement.preventDefault();
      if (avecRecherche) {
        // La liste a une barre de recherche : la lettre l'AMORCE, plutôt que de
        // sauter. Elle ouvre le panneau, le focus part dans le champ, et la
        // frappe suivante continue le mot — c'est ce qu'un opérateur attend
        // d'un contrôle qui cherche.
        setRecherche(evenement.key);
        setActif(0);
        ouvrirSur(0);
        return;
      }
      const rang = rangParLettre(evenement.key, ouvert ? actif : depart - 1);
      if (rang < 0) return;
      if (!ouvert) ouvrirSur(rang);
      else setActif(rang);
    },
    [
      actif,
      affichees.length,
      avecRecherche,
      choisi,
      choisirLeRang,
      deplacer,
      fermer,
      fermerEtRevenir,
      ouvert,
      ouvrirSur,
      rangParLettre,
    ],
  );

  /**
   * Le clavier du **champ de recherche**.
   *
   * Les flèches et `Entrée` ne tapent pas : elles pilotent la liste. Le focus,
   * lui, ne bouge pas — c'est `aria-activedescendant` qui suit la surbrillance,
   * et c'est la seule façon pour un lecteur d'écran d'annoncer l'option visée
   * sans que l'opérateur perde le champ où il tape.
   */
  const auClavierDuChamp = useCallback(
    (evenement: KeyboardEvent<HTMLInputElement>) => {
      switch (evenement.key) {
        case 'ArrowDown':
          evenement.preventDefault();
          deplacer(1);
          return;
        case 'ArrowUp':
          evenement.preventDefault();
          deplacer(-1);
          return;
        case 'Home':
          // Sans `preventDefault` : dans un champ de saisie, `Début` et `Fin`
          // appartiennent au curseur de texte, pas à la liste.
          return;
        case 'Enter':
          evenement.preventDefault();
          choisirLeRang(actif);
          return;
        case 'Escape':
          evenement.preventDefault();
          fermerEtRevenir();
          return;
        case 'Tab':
          fermer();
          return;
        default:
      }
    },
    [actif, choisirLeRang, deplacer, fermer, fermerEtRevenir],
  );

  const lignes = useMemo(
    () =>
      affichees.map((option, rang) => (
        <li
          className="filter-panel__option"
          key={option.value}
          id={idOption(rang)}
          role="option"
          aria-selected={option.value === value}
          data-active={rang === actif ? 'true' : undefined}
          // Un `click` franc, et **aucun `preventDefault`**.
          //
          // La première écriture retenait le focus sur le déclencheur en
          // annulant le `mousedown` : le focus ne bougeait donc jamais, et le
          // retour du focus de `fermerEtRevenir` devenait du code mort — une
          // mutation l'a montré en passant au VERT. Le focus part maintenant
          // pour de bon quand on clique une ligne, et c'est `fermerEtRevenir`
          // qui le ramène. La garantie est la même pour l'opérateur ; la
          // différence est qu'elle est désormais TENUE par du code qu'un
          // contrôle peut casser.
          onClick={() => {
            choisirLeRang(rang);
          }}
        >
          {option.label}
        </li>
      )),
    // `idPanneau` et non `idOption` : le second est une fonction pure du
    // premier, recréée à chaque rendu, et le lier ferait rebâtir la liste sans
    // rien y changer.
    [actif, affichees, choisirLeRang, idPanneau, value],
  );

  /** Le nom accessible de la liste : le libellé du champ, et ce qu'elle contient. */
  const nomDeLaListe = t('filter.listLabel', {
    label: t(labelKey),
    count: affichees.length,
  });

  const liste = (
    <ul
      className={avecRecherche ? 'filter-panel__list' : 'filter-panel filter-panel--list'}
      id={idListe}
      role="listbox"
      aria-label={avecRecherche ? nomDeLaListe : undefined}
      aria-labelledby={avecRecherche ? undefined : `${controlId}-libelle`}
    >
      {lignes}
    </ul>
  );

  return (
    <FilterPill
      triggerId={controlId}
      labelId={`${controlId}-libelle`}
      valueId={`${controlId}-valeur`}
      labelKey={labelKey}
      display={affichee}
      panelId={idPanneau}
      expanded={ouvert}
      haspopup="listbox"
      // Le rôle `combobox` va à l'élément qui porte le focus quand le panneau
      // est ouvert. Avec une barre de recherche, c'est le CHAMP ; le
      // déclencheur redevient alors un bouton qui déploie. Deux `combobox`
      // imbriqués seraient une faute du patron, pas une redondance.
      role={avecRecherche ? undefined : 'combobox'}
      activeDescendant={avecRecherche ? undefined : idOption(actif)}
      invalid={invalid}
      describedBy={describedBy}
      triggerRef={declencheur}
      containerRef={contenant}
      onToggle={() => {
        if (ouvert) fermer();
        else ouvrirSur(choisi < 0 ? 0 : choisi);
      }}
      onKeyDown={auClavier}
    >
      {ouvert ? (
        avecRecherche ? (
          <div className="filter-panel filter-panel--search" id={idPanneau}>
            <input
              className="filter-panel__search"
              id={idChamp}
              ref={champ}
              type="text"
              role="combobox"
              autoComplete="off"
              aria-expanded
              aria-controls={idListe}
              // **Le fil qui casse en premier dans les listes bâties à la
              // main.** Le focus est ici ; sans cet attribut, l'option atteinte
              // à la flèche n'est annoncée par personne, et la liste devient
              // muette pour qui ne la voit pas.
              aria-activedescendant={affichees.length === 0 ? undefined : idOption(actif)}
              aria-label={t('filter.searchLabel', { label: t(labelKey) })}
              placeholder={t('filter.searchPlaceholder')}
              value={recherche}
              onChange={(evenement) => {
                setRecherche(evenement.target.value);
                // La surbrillance repart en tête : la garder au troisième rang
                // la poserait sur une option que la nouvelle saisie ne montre
                // plus.
                setActif(0);
              }}
              onKeyDown={auClavierDuChamp}
            />
            {affichees.length === 0 ? (
              // Une boîte vide se lit comme un contrôle cassé. Ce qui manque se
              // dit, ici comme ailleurs.
              <p className="filter-panel__empty">{t('filter.noMatch')}</p>
            ) : (
              liste
            )}
          </div>
        ) : (
          liste
        )
      ) : null}
      {avecRecherche ? (
        // La région vivante — masquée à l'œil, lue par les lecteurs d'écran.
        // Le nombre de correspondances change à chaque frappe, et sans lui un
        // opérateur non voyant taperait sans savoir s'il reste quelque chose.
        <span className="filter-pill__reading" role="status" aria-live="polite">
          {ouvert ? t('filter.matchCount', { count: affichees.length }) : ''}
        </span>
      ) : null}
    </FilterPill>
  );
}
